import { useEffect, useMemo, useState } from 'react';

import type { LoaderFunctionArgs } from 'react-router';
import { MetaFunction, Outlet, data, useLocation } from 'react-router';

import { AnalyticsProvider, useAnalytics } from '@boc/analyticsV3';
import { Duration } from '@boc/duration';
import { useIdentity } from '@boc/framework/auth/context';
import { hashEmail } from '@boc/framework/auth/hashing';
import { ChannelProvider } from '@boc/framework/channels/provider';
import { CartProvider } from '@boc/framework/checkout/cart/provider';
import { getAnonClient } from '@boc/framework/client';
import { generateCacheControlV2 } from '@boc/framework/headers/cache';
import type {
  CollectionProductsBySlugQuery,
  CollectionProductsBySlugQueryVariables,
} from '@boc/framework/saleor-graphql';
import { CollectionProductsBySlugDocument } from '@boc/framework/saleor-graphql';

import type { Route } from './+types/layout';

import secrets from '~/secrets.server';

import { cacheConfig } from '~/cache-config';
import config from '~/config';
import { cn } from '~/styling';
import type { IHandle, TFMLinks } from '~/types';
import { parseParams, urls } from '~/urls';

import { getCache } from '~/framework.server/cache';

import { isMissingPiecesAvailable } from '~/lib/server/missing-pieces.server';

import { getMegaMenu } from '~/local/base/data';
import Footer from '~/local/components/footer';
import { FreeShippingBar } from '~/local/components/free-shipping-bar';
import Header from '~/local/components/header';
import MegaMenu from '~/local/components/main-nav';
import MobileMenu from '~/local/components/mobile-menu';
import { NotificationBar } from '~/local/components/notificationBar';
import { variantCopy } from '~/local/custom-puzzle/variants';
// import { Banner } from '~/local/homepage/components/banner';
import { SearchSidebar } from '~/local/products/search/components/search';

import { navHeader } from '#/brand/navigation';
import { defaultOgDescription, defaultOgTitle, defaultTitle } from '#/seo';

export const shouldRevalidate = () => false;

export const handle = {
  header: {
    bgColour: 'bg-primary-2-10',
  },
  bgColour: 'bg-primary-2-10',
} satisfies IHandle;

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const client = getAnonClient({
    apiUrl: secrets.SALEOR_API_URL,
  });

  const { channel, saleorLocale } = parseParams(params);

  const tfmLinks: TFMLinks[] = [];

  tfmLinks.push({
    title: 'Custom Puzzles',
    href: urls.customPuzzle.landing(params, {}),
    children: Object.entries(variantCopy).map(([slug, { title }]) => {
      return {
        title,
        href: urls.customPuzzle.landing(params, { variant: slug }),
      };
    }),
  });

  const bestsellers = await client.request<
    CollectionProductsBySlugQuery,
    CollectionProductsBySlugQueryVariables
  >(CollectionProductsBySlugDocument, {
    slug: 'bestsellers',
    channel: channel.slug,
    locale: saleorLocale,
    first: 10,
    filter: { isPublished: true, isVisibleInListing: true },
  });

  const megamenu = await getCache().getOrSet(
    cacheConfig.megamenu.key(channel, saleorLocale),
    async () => getMegaMenu(params),
    cacheConfig.megamenu.ttl
  );

  return data(
    {
      tfmLinks,
      bestsellers: bestsellers.collection,
      megamenu,
      missingPiecesAvailable: isMissingPiecesAvailable(),
    },
    {
      headers: {
        'Cache-Control': generateCacheControlV2({ sharedMaxAge: Duration.days(1) }),
      },
    }
  );
};

export const meta: MetaFunction = ({ params }) => {
  const { channel } = parseParams(params);

  return [
    { title: channel.seo?.title ? channel.seo.title(defaultTitle) : defaultTitle },
    {
      property: 'og:title',
      content: channel.seo?.ogTitle ? channel.seo.ogTitle(defaultOgTitle) : defaultOgTitle,
    },
    {
      name: 'description',
      content: channel.seo?.description ? channel.seo.description(defaultTitle) : defaultTitle,
    },
    {
      property: 'og:description',
      content: channel.seo?.description
        ? channel.seo.description(defaultOgDescription)
        : defaultOgDescription,
    },
  ];
};

function AnalyticsTracking() {
  const { identity } = useIdentity();
  const tracker = useAnalytics();
  const location = useLocation();

  // Track page views on navigation
  useEffect(() => {
    tracker.trackPageView({ url: location.pathname });
  }, [location.pathname, tracker]);

  // Track identify event when user identity is available
  useEffect(() => {
    if (!identity?.email) return;

    hashEmail(identity.email).then((hash: string) => {
      tracker.identify({
        email: identity.email!,
        data: {
          visitorEmailHash: hash,
          customerFirstName: identity.firstName,
          customerLastName: identity.lastName,
        },
      });
    });
  }, [identity, tracker]);

  return null;
}

export default function Layout({ matches, params, loaderData }: Route.ComponentProps) {
  const { channel, saleorLocale, locale } = parseParams(params);
  const { tfmLinks, bestsellers, megamenu, missingPiecesAvailable } = loaderData;

  const [showSearch, setShowSearch] = useState(false);

  const configHeader = (
    matches.find((match) => (match?.handle as IHandle)?.header)?.handle as IHandle
  )?.header;
  const configFooter = (
    matches.find((match) => (match?.handle as IHandle)?.header)?.handle as IHandle
  )?.footer;
  const configMainNav = (
    matches.find((match) => (match?.handle as IHandle)?.header)?.handle as IHandle
  )?.mainNav;
  const notification = (
    matches.find((match) => (match?.handle as IHandle)?.notification)?.handle as IHandle
  )?.notification;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { identity } = useIdentity();

  // const hideBanner = matches.some(
  //   (match) => match.pathname.includes('/custom-puzzle') || match.pathname.includes('/checkout')
  // );

  const client = useMemo(
    () =>
      getAnonClient({
        apiUrl: config.PUBLIC_SALEOR_API_URL,
      }),
    []
  );

  return (
    <ChannelProvider channel={channel} locale={locale} saleorLocale={saleorLocale}>
      <AnalyticsProvider
        channel={channel}
        gaId={config.PUBLIC_GA_MEASUREMENT_ID}
        klaviyoId={config.PUBLIC_KLAVIYO_ID}
        fbPixelId={config.PUBLIC_FB_PIXEL_ID}
        interaktToken={config.PUBLIC_INTERAKT_TOKEN}
        debug={config.PUBLIC_NODE_ENV === 'development'}
      >
        <CartProvider
          client={client}
          channel={channel}
          locale={saleorLocale}
          userEmail={identity?.email}
        >
          <AnalyticsTracking />
          <SearchSidebar
            show={showSearch}
            setShow={setShowSearch}
            bestsellers={bestsellers?.products?.edges.map((edge) => edge.node)}
          />
          <div className="flex min-h-screen flex-col">
            {/* Route-specific notices take precedence over the standing free shipping promo. */}
            {notification ? (
              <NotificationBar>{notification}</NotificationBar>
            ) : (
              <FreeShippingBar channel={channel} />
            )}

            <div
              className={cn(
                'relative flex h-14 overflow-visible sm:h-16',
                configHeader?.bgColour || 'bg-current'
              )}
            >
              <Header
                showSearch={showSearch}
                setShowSearch={setShowSearch}
                setMobileMenuOpen={configMainNav?.hide ? null : setMobileMenuOpen}
                navigation={navHeader}
              />
            </div>

            {configMainNav?.hide ? null : <MegaMenu menu={megamenu} />}

            <MobileMenu
              navigation={navHeader}
              menu={megamenu}
              show={mobileMenuOpen}
              setShow={setMobileMenuOpen}
            />

            {/* {!hideBanner && (
            <Banner
              primaryText="COMIC-CON IS ON"
              secondaryText={
                <p>
                  We wish to inform ya'll that any orders placed between <b>18th December</b> and{' '}
                  <b>21st December</b> will be attended to on <b>22nd December</b> as we will be
                  attending Bengaluru Comic-Con 2025! Visit us at{' '}
                  <a
                    href="https://www.comicconindia.com/all-cities-main-page/bengaluru-comic-con"
                    className="font-extrabold hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    KTPO Park
                  </a>{' '}
                  for exclusive previews, special offers, and more.
                </p>
              }
            />
          )} */}

            <div className="container mx-auto max-w-screen-xl flex-1 px-6">
              <Outlet />
            </div>

            {configFooter?.hide ? null : (
              <Footer tfmLinks={tfmLinks} missingPiecesAvailable={missingPiecesAvailable} />
            )}
          </div>
        </CartProvider>
      </AnalyticsProvider>
    </ChannelProvider>
  );
}
